/* eslint-disable react/forbid-component-props */
import { ComponentProps, ReactElement } from 'react';
import {
    Document,
    Image,
    Page,
    StyleSheet,
    Text,
    View,
} from '@react-pdf/renderer';
import { fitImageBox, footerImageBox, InvoiceImageSize } from '../invoiceImages';
import { Invoice } from '../types';
import { getPrestigeInvoiceModel, PrestigeInvoiceModel } from './prestige';

const ROW_HEIGHT = 16;
const SPACER_WIDTH = 34;
const TAXED_WIDTH = 42;
const AMOUNT_WIDTH = 68;
const TABLE_WIDTH = 540;
const TOTALS_WIDTH = 170;
const FOOTER_WIDTH = TABLE_WIDTH - TOTALS_WIDTH;
const BILL_COLUMN_WIDTHS = [178, 168, 189];
const BILL_PAIR_WIDTHS = [267, 268];

const styles = StyleSheet.create({
    page: {
        fontFamily: 'Helvetica',
        color: '#111111',
        paddingTop: 28,
        paddingHorizontal: 36,
        paddingBottom: 28,
        position: 'relative',
        flexDirection: 'column',
    },
    header: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
    },
    logo: {
        width: 270,
        paddingRight: 8,
    },
    wordmark: {
        fontSize: 18,
        fontFamily: 'Helvetica-Bold',
    },
    sellerLines: {
        marginTop: 8,
    },
    sellerLine: {
        marginTop: 3,
        fontSize: 8,
        color: '#111111',
    },
    heading: {
        width: 220,
        alignItems: 'flex-end',
        paddingTop: 8,
    },
    title: {
        fontSize: 22,
        fontFamily: 'Helvetica-Bold',
        color: '#C04E4E',
        textAlign: 'right',
    },
    dateRow: {
        flexDirection: 'row',
        alignItems: 'flex-start',
        justifyContent: 'flex-end',
        marginTop: 10,
    },
    dateLabelWrap: {
        height: 16,
        marginTop: 0.5,
        marginRight: 8,
        justifyContent: 'center',
    },
    dateLabel: {
        fontSize: 11,
        lineHeight: 1,
        fontFamily: 'Helvetica-Bold',
        color: '#111111',
    },
    dateBox: {
        width: 92,
        borderWidth: 1,
        borderColor: '#9A9A9A',
    },
    dateValue: {
        height: 16,
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#ffffff',
    },
    dateText: {
        fontSize: 10,
        fontFamily: 'Helvetica-Bold',
        textAlign: 'center',
    },
    dateAccent: {
        height: 14,
        backgroundColor: '#D99595',
    },
    billTo: {
        marginTop: 18,
        marginBottom: 4,
    },
    billToLabel: {
        fontSize: 8,
        fontFamily: 'Helvetica-Bold',
        color: '#ffffff',
        letterSpacing: 0.6,
    },
    billToMark: {
        width: BILL_COLUMN_WIDTHS[0] + BILL_COLUMN_WIDTHS[1],
        height: 14,
        backgroundColor: '#000000',
        justifyContent: 'center',
        paddingLeft: 6,
    },
    billRows: {
        marginTop: 8,
    },
    billRow: {
        flexDirection: 'row',
        alignItems: 'flex-start',
        marginBottom: 6,
    },
    billLabel: {
        fontSize: 8,
        fontFamily: 'Helvetica',
        color: '#111111',
    },
    billValue: {
        fontSize: 8,
        fontFamily: 'Helvetica',
        color: '#111111',
    },
    table: {
        marginTop: 10,
        width: TABLE_WIDTH,
        flexGrow: 1,
        flexDirection: 'column',
    },
    tableHeader: {
        flexDirection: 'row',
        alignItems: 'stretch',
        backgroundColor: '#000000',
        height: 15,
    },
    tableBody: {
        flexGrow: 1,
        flexDirection: 'column',
        position: 'relative',
    },
    noteOverlay: {
        position: 'absolute',
        left: 0,
        paddingTop: 6,
        paddingHorizontal: 8,
        overflow: 'hidden',
    },
    tableRow: {
        flexDirection: 'row',
        alignItems: 'stretch',
        flexGrow: 1,
        minHeight: ROW_HEIGHT,
        borderBottomWidth: 0.8,
        borderBottomColor: '#000000',
        borderStyle: 'dotted',
    },
    rowAlt: {
        backgroundColor: '#F2F2F2',
    },
    rowShade: {
        backgroundColor: '#D9D9D9',
    },
    cell: {
        justifyContent: 'center',
        paddingHorizontal: 3,
    },
    spacer: {
        width: SPACER_WIDTH,
        borderLeftWidth: 0.8,
        borderRightWidth: 0.8,
        borderColor: '#000000',
        borderStyle: 'dotted',
    },
    ruled: {
        borderLeftWidth: 0.8,
        borderLeftColor: '#000000',
        borderStyle: 'dotted',
    },
    rowEdgeLeft: {
        borderLeftWidth: 0.8,
        borderLeftColor: '#000000',
        borderStyle: 'dotted',
    },
    rowEdgeRight: {
        borderRightWidth: 0.8,
        borderRightColor: '#000000',
        borderStyle: 'dotted',
    },
    headerText: {
        fontSize: 8,
        fontFamily: 'Helvetica-Bold',
        color: '#ffffff',
    },
    cellText: {
        fontSize: 8,
    },
    noteText: {
        fontSize: 8,
        fontFamily: 'Helvetica',
        color: '#1A56C4',
        textAlign: 'center',
        width: '100%',
        lineHeight: 2.66,
    },
    summaryPage: {
        flexDirection: 'row',
        flexWrap: 'nowrap',
        justifyContent: 'flex-start',
        alignItems: 'center',
        width: TABLE_WIDTH,
        marginTop: 8,
    },
    footerImages: {
        width: FOOTER_WIDTH,
        alignItems: 'flex-start',
        justifyContent: 'center',
    },
    footerLine: {
        fontSize: 8,
        color: '#333333',
        marginTop: 4,
    },
    totals: {
        width: TOTALS_WIDTH,
        marginTop: 8,
    },
    summaryRow: {
        flexDirection: 'row',
        justifyContent: 'flex-end',
        alignItems: 'center',
        marginBottom: 4,
    },
    summaryLabel: {
        fontSize: 9,
        width: 70,
        textAlign: 'right',
        marginRight: 8,
    },
    summaryValue: {
        fontSize: 9,
        width: 78,
        textAlign: 'right',
    },
    totalLabel: {
        fontSize: 11,
        fontFamily: 'Helvetica-Bold',
        width: 70,
        textAlign: 'right',
        marginRight: 8,
    },
    totalSymbol: {
        fontSize: 11,
        fontFamily: 'Helvetica-Bold',
        marginRight: 4,
    },
    totalBox: {
        width: 78,
        height: 16,
        backgroundColor: '#D99595',
        alignItems: 'center',
        justifyContent: 'center',
    },
    totalValue: {
        fontSize: 9,
        textAlign: 'center',
    },
    watermark: {
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        alignItems: 'center',
        justifyContent: 'center',
    },
    watermarkText: {
        fontSize: 86,
        letterSpacing: 10,
        color: 'rgba(232, 80, 91, 0.22)',
        fontFamily: 'Helvetica-Bold',
        transform: 'rotate(-35deg)',
    },
});

const createPdfOwnerPassword = () => {
    const bytes = crypto.getRandomValues(new Uint8Array(32));
    return Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
};

const logoStyle = (
    imageUrl: string,
    imageSizes: Record<string, InvoiceImageSize>,
): ComponentProps<typeof Image>['style'] => {
    const measured = imageSizes[imageUrl];
    const box = measured
        ? fitImageBox(measured.width, measured.height, 250, 96)
        : undefined;
    if (!box) return { width: 230, maxHeight: 96 };
    return { width: box.width, height: box.height };
};

const footerStyle = (
    imageUrl: string,
    imageSizes: Record<string, InvoiceImageSize>,
): ComponentProps<typeof Image>['style'] => {
    const measured = imageSizes[imageUrl];
    const box = measured
        ? footerImageBox(measured.width, measured.height, FOOTER_WIDTH, 'full')
        : undefined;
    if (!box) return { width: FOOTER_WIDTH, alignSelf: 'flex-start' };
    return { width: box.width, height: box.height, alignSelf: 'flex-start' };
};

const billCellWidth = (
    row: Array<PrestigeInvoiceModel['billToRows'][number][number]>,
    index: number,
) => {
    const widths = row.length > 2 ? BILL_COLUMN_WIDTHS : BILL_PAIR_WIDTHS;
    let width = widths[index] || 0;
    for (let next = index + 1; next < row.length; next += 1) {
        if (row[next]) break;
        width += widths[next] || 0;
    }
    return width;
};

const BillTo = ({ model }: { model: PrestigeInvoiceModel }) => (
    <View style={styles.billTo}>
        <View style={styles.billToMark}>
            <Text style={styles.billToLabel}>{model.billToLabel}</Text>
        </View>
        <View style={styles.billRows}>
            {model.billToRows.map((row, rowIndex) => (
                <View
                    // eslint-disable-next-line react/no-array-index-key
                    key={`bill-${rowIndex}`}
                    style={styles.billRow}
                >
                    {row.map((field, index) => (
                        field ? (
                            <View
                                // eslint-disable-next-line react/no-array-index-key
                                key={`bill-${rowIndex}-${index}`}
                                style={{ width: billCellWidth(row, index), paddingRight: 8 }}
                            >
                                <Text>
                                    <Text style={styles.billLabel}>{`${field.label}: `}</Text>
                                    <Text style={styles.billValue}>{field.value}</Text>
                                </Text>
                            </View>
                        ) : null
                    ))}
                </View>
            ))}
        </View>
    </View>
);

const NOTE_ROW_GAP = 3;

const descriptionColumnWidth = (model: PrestigeInvoiceModel) => {
    let used = model.showColumnSpacer ? SPACER_WIDTH : 0;
    let flexColumns = 0;
    model.columns.forEach((column) => {
        if (column.flex) {
            flexColumns += 1;
            return;
        }
        used += column.align === 'right' ? AMOUNT_WIDTH : TAXED_WIDTH;
    });
    const remaining = TABLE_WIDTH - used;
    return Math.max(120, remaining / (flexColumns || 1));
};

const tableCells = (
    model: PrestigeInvoiceModel,
    values: string[] | null,
    bold: boolean,
) => {
    const textStyle = bold ? styles.headerText : styles.cellText;
    const cells: ReactElement[] = [];
    model.columns.forEach((column, index) => {
        if (model.showColumnSpacer && index === 1) {
            cells.push(<View key={`${column.key}-spacer`} style={bold ? { width: SPACER_WIDTH } : styles.spacer} />);
        }
        const ruled = !bold && !column.flex && !(model.showColumnSpacer && index === 1);
        const last = index === model.columns.length - 1;
        let width = TAXED_WIDTH;
        if (column.flex) width = descriptionColumnWidth(model);
        else if (column.align === 'right') width = AMOUNT_WIDTH;
        const { align: columnAlign } = column;
        let align: 'left' | 'center' | 'right' = columnAlign;
        if (bold && columnAlign === 'right') align = 'center';
        const cellValue = values ? values[index] || '' : column.label;
        cells.push(
            <View
                key={column.key}
                style={[
                    styles.cell,
                    { width },
                    ruled ? styles.ruled : {},
                    !bold && column.flex ? styles.rowEdgeLeft : {},
                    !bold && last ? styles.rowEdgeRight : {},
                ]}
            >
                <Text style={[textStyle, { textAlign: align, width: '100%' }]}>
                    {cellValue}
                </Text>
            </View>,
        );
    });
    return cells;
};

const noteTop = (model: PrestigeInvoiceModel) => {
    const filled = model.rows.filter((row) => row.cells.some((cell) => cell)).length;
    const start = Math.min(filled + NOTE_ROW_GAP, Math.max(model.rows.length - 1, 0));
    return `${(start / Math.max(model.rows.length, 1)) * 100}%`;
};

const LineTable = ({ model }: { model: PrestigeInvoiceModel }) => (
    <View style={styles.table}>
        <View style={styles.tableHeader}>
            {tableCells(model, null, true)}
        </View>
        <View style={styles.tableBody}>
            {model.rows.map((row, rowIndex) => (
                <View
                    key={row.key}
                    style={[
                        styles.tableRow,
                        rowIndex % 2 === 0 ? styles.rowAlt : styles.rowShade,
                    ]}
                >
                    {tableCells(model, row.cells, false)}
                </View>
            ))}
            {model.note ? (
                <View style={[styles.noteOverlay, {
                    top: noteTop(model),
                    width: descriptionColumnWidth(model),
                    bottom: 4,
                }]}
                >
                    <Text style={styles.noteText}>{model.note.trim()}</Text>
                </View>
            ) : null}
        </View>
    </View>
);

const PrestigeInvoiceDocument = ({
    invoice,
    imageSizes,
}: {
    invoice: Invoice;
    imageSizes: Record<string, InvoiceImageSize>;
}) => {
    const model = getPrestigeInvoiceModel(invoice);
    const ownerPassword = createPdfOwnerPassword();
    const { seller } = invoice.document.design;

    return (
        <Document
            title={`${model.title} ${invoice.number}`.trim()}
            author={seller.tradingName || seller.legalName}
            ownerPassword={ownerPassword}
            permissions={{
                printing: 'highResolution',
                modifying: false,
                copying: false,
                annotating: false,
                fillingForms: false,
                contentAccessibility: true,
                documentAssembly: false,
            }}
        >
            <Page size="LETTER" style={styles.page}>
                {model.isVoid ? (
                    <View style={styles.watermark} fixed>
                        <Text style={styles.watermarkText}>VOID</Text>
                    </View>
                ) : null}
                <View style={styles.header}>
                    <View style={styles.logo}>
                        {model.logoImageUrl ? (
                            <Image
                                src={model.logoImageUrl}
                                style={logoStyle(model.logoImageUrl, imageSizes)}
                            />
                        ) : null}
                        {model.wordmark ? (
                            <Text style={styles.wordmark}>{model.wordmark}</Text>
                        ) : null}
                        {model.sellerLines.length ? (
                            <View style={styles.sellerLines}>
                                {model.sellerLines.map((line) => (
                                    <Text key={line} style={styles.sellerLine}>{line}</Text>
                                ))}
                            </View>
                        ) : null}
                    </View>
                    <View style={styles.heading}>
                        <Text style={styles.title}>{model.title}</Text>
                        <View style={styles.dateRow}>
                            <View style={styles.dateLabelWrap}>
                                <Text style={styles.dateLabel}>{model.dateLabel}</Text>
                            </View>
                            <View style={styles.dateBox}>
                                <View style={styles.dateValue}>
                                    <Text style={styles.dateText}>{model.issuedOn}</Text>
                                </View>
                                <View style={styles.dateAccent} />
                            </View>
                        </View>
                    </View>
                </View>
                <BillTo model={model} />
                <LineTable model={model} />
                <View style={styles.summaryPage} wrap={false}>
                    <View style={styles.footerImages}>
                        {model.footerImages.map((image) => (
                            <Image
                                key={image.id}
                                src={image.imageUrl}
                                style={footerStyle(image.imageUrl, imageSizes)}
                            />
                        ))}
                        {model.footerLines.map((line, index) => (
                            // eslint-disable-next-line react/no-array-index-key
                            <Text key={`${index}-${line}`} style={styles.footerLine}>{line}</Text>
                        ))}
                    </View>
                    <View style={styles.totals}>
                        {model.summary.map((row) => (
                            <View key={row.key} style={styles.summaryRow}>
                                <Text style={styles.summaryLabel}>{row.label}</Text>
                                <Text style={styles.summaryValue}>{row.value}</Text>
                            </View>
                        ))}
                        <View style={styles.summaryRow}>
                            <Text style={styles.totalLabel}>TOTAL</Text>
                            <Text style={styles.totalSymbol}>{model.currencySymbol}</Text>
                            <View style={styles.totalBox}>
                                <Text style={styles.totalValue}>{model.total}</Text>
                            </View>
                        </View>
                    </View>
                </View>
            </Page>
        </Document>
    );
};

export default PrestigeInvoiceDocument;
